import React from 'react';
import { DailySummary } from '@/types/record';
import { getCategoryMeta } from '@/utils/bpClassification';
import { getFluctuationEvaluation, getPulsePressureEvaluation } from '@/utils/calculations';
import { StampBadge } from '@/components/common/StampBadge';

export interface DailySummaryCardProps {
  summary: DailySummary;
  variant?: 'full' | 'compact' | 'banner';
  showCategory?: boolean;
  className?: string;
}

export const DailySummaryCard: React.FC<DailySummaryCardProps> = ({
  summary,
  variant = 'full',
  showCategory = true,
  className = '',
}) => {
  const categoryMeta = summary.category ? getCategoryMeta(summary.category) : null;
  const fluctuationEval = getFluctuationEvaluation(summary.fluctuation_systolic);
  const ppEval = summary.avg_pulse_pressure ? getPulsePressureEvaluation(summary.avg_pulse_pressure) : null;

  if (variant === 'banner') {
    return (
      <div
        className={`
          p-3 bg-paper-100 border border-paper-400 rounded-xs flex flex-wrap items-center justify-between gap-3
          ${className}
        `}
      >
        <div className="flex items-center gap-3">
          <div className="space-y-0.5">
            <div className="flex items-center gap-2">
              <span className="font-serif font-bold text-xs text-ink-700">當日平均:</span>
              <span className="font-mono font-black text-base text-ink-900">
                {summary.avg_systolic} / {summary.avg_diastolic}
              </span>
              <span className="font-mono text-xs text-ink-500">mmHg</span>
            </div>
            <span className="font-serif text-[11px] text-ink-500">
              心跳平均 {summary.avg_pulse} bpm • 共 {summary.count} 筆
            </span>
          </div>
          {showCategory && categoryMeta && (
            <StampBadge category={categoryMeta.category} size="sm" />
          )}
        </div>

        <div className="flex items-center gap-2">
          <span
            className={`font-serif text-[11px] px-2 py-0.5 border rounded-xs ${fluctuationEval.colorClass}`}
          >
            {fluctuationEval.labelZh} (Δ {summary.fluctuation_systolic} mmHg)
          </span>
        </div>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div
        className={`
          p-3.5 bg-paper-200/70 border border-paper-300 rounded-xs space-y-2.5
          ${className}
        `}
      >
        <div className="flex items-center justify-between">
          <span className="font-serif text-xs font-bold uppercase tracking-wider text-ink-700">
            當日平均統計 ({summary.count} 次量測)
          </span>
          {showCategory && categoryMeta && (
            <StampBadge category={categoryMeta.category} size="sm" />
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
          <div className="p-2 bg-white border border-paper-300 rounded-xs">
            <span className="block font-serif text-[10px] text-ink-500 uppercase">平均收縮壓</span>
            <span className="font-mono font-bold text-lg text-ink-900">{summary.avg_systolic}</span>
            <span className="text-[10px] text-ink-400 font-mono"> mmHg</span>
          </div>
          <div className="p-2 bg-white border border-paper-300 rounded-xs">
            <span className="block font-serif text-[10px] text-ink-500 uppercase">平均舒張壓</span>
            <span className="font-mono font-bold text-lg text-ink-900">{summary.avg_diastolic}</span>
            <span className="text-[10px] text-ink-400 font-mono"> mmHg</span>
          </div>
          <div className="p-2 bg-white border border-paper-300 rounded-xs">
            <span className="block font-serif text-[10px] text-ink-500 uppercase">平均心跳</span>
            <span className="font-mono font-bold text-lg text-ink-900">{summary.avg_pulse}</span>
            <span className="text-[10px] text-ink-400 font-mono"> bpm</span>
          </div>
          <div className="p-2 bg-white border border-paper-300 rounded-xs">
            <span className="block font-serif text-[10px] text-ink-500 uppercase">收縮壓波動</span>
            <span className="font-mono font-bold text-lg text-ink-900">Δ {summary.fluctuation_systolic}</span>
            <span className="text-[10px] text-ink-400 font-mono"> mmHg</span>
          </div>
        </div>
      </div>
    );
  }

  // Full Variant
  return (
    <div
      className={`
        p-4 bg-paper-100 border border-paper-400 rounded-xs space-y-4 shadow-paper-sm
        ${className}
      `}
    >
      {/* Header Row */}
      <div className="flex flex-wrap items-center justify-between gap-2 pb-2.5 border-b border-paper-300">
        <div className="flex items-center gap-2">
          <span className="font-serif font-bold text-xs uppercase tracking-wider text-ink-800">
            📊 當日醫學統計摘要 • DAILY CLINICAL SUMMARY
          </span>
          <span className="font-serif text-[11px] text-ink-500 bg-paper-200 px-2 py-0.5 rounded-xs border border-paper-300">
            共 {summary.count} 筆紀錄
          </span>
        </div>

        {showCategory && categoryMeta && (
          <StampBadge category={categoryMeta.category} size="md" />
        )}
      </div>

      {/* Main Metrics 3-Column Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Metric 1: Average Blood Pressure */}
        <div className="p-3 bg-white border border-paper-300 rounded-xs space-y-1">
          <span className="font-serif text-xs text-ink-500 font-semibold block">
            當日平均血壓 (AVG BP)
          </span>
          <div className="flex items-baseline gap-1">
            <span className="font-mono font-black text-2xl text-ink-900">
              {summary.avg_systolic}
            </span>
            <span className="text-ink-400 font-serif">/</span>
            <span className="font-mono font-bold text-xl text-ink-800">
              {summary.avg_diastolic}
            </span>
            <span className="font-mono text-xs text-ink-500 ml-1">mmHg</span>
          </div>
          <div className="font-mono text-[11px] text-ink-500 pt-1">
            <span>脈壓差: {summary.avg_pulse_pressure ?? (summary.avg_systolic - summary.avg_diastolic)} mmHg</span>
          </div>
        </div>

        {/* Metric 2: Average Heart Rate */}
        <div className="p-3 bg-white border border-paper-300 rounded-xs space-y-1">
          <span className="font-serif text-xs text-ink-500 font-semibold block">
            平均心跳脈搏 (AVG PULSE)
          </span>
          <div className="flex items-baseline gap-1">
            <span className="font-mono font-black text-2xl text-ink-900">
              {summary.avg_pulse}
            </span>
            <span className="font-mono text-xs text-ink-500 ml-1">bpm</span>
          </div>
          <div className="font-mono text-[11px] text-ink-500 pt-1">
            <span>平均動脈壓 (MAP): {summary.avg_map ?? Math.round(summary.avg_diastolic + (summary.avg_systolic - summary.avg_diastolic) / 3)} mmHg</span>
          </div>
        </div>

        {/* Metric 3: Fluctuation Assessment */}
        <div className="p-3 bg-white border border-paper-300 rounded-xs space-y-1">
          <div className="flex items-center justify-between">
            <span className="font-serif text-xs text-ink-500 font-semibold">
              單日波動狀態 (FLUCTUATION)
            </span>
            <span
              className={`font-serif text-[10px] px-1.5 py-0.2 border rounded-xs font-bold ${fluctuationEval.colorClass}`}
            >
              {fluctuationEval.labelZh}
            </span>
          </div>
          <div className="space-y-0.5 text-xs font-mono text-ink-800 pt-1">
            <div>
              收縮壓: <span className="font-bold">Δ {summary.fluctuation_systolic} mmHg</span>{' '}
              <span className="text-ink-400 text-[11px]">({summary.min_systolic} ~ {summary.max_systolic})</span>
            </div>
            <div>
              舒張壓: <span className="font-bold">Δ {summary.fluctuation_diastolic} mmHg</span>{' '}
              <span className="text-ink-400 text-[11px]">({summary.min_diastolic} ~ {summary.max_diastolic})</span>
            </div>
          </div>
        </div>
      </div>

      {/* Clinical Notes & Recommendations Footer */}
      {categoryMeta && (
        <div className="p-2.5 bg-paper-200/80 border border-paper-300 rounded-xs text-xs font-serif text-ink-700 leading-relaxed">
          <span className="font-bold text-ink-900 mr-1">臨床指引提示:</span>
          {categoryMeta.recommendation}
          {ppEval && ppEval.isHigh && (
            <span className="text-red-700 ml-1">({ppEval.description})</span>
          )}
        </div>
      )}
    </div>
  );
};
